import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, MessageCircle, Plus, Pencil, Trash2, Save, X,
  Users, User, Copy, ChevronDown, ChevronUp, Send, Clock, CheckCircle, XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_LABELS = {
  welcome_order: 'Bienvenida y confirmacion de pedido',
  contributor_invitation: 'Invitacion a colaborador',
  contributor_reminder: 'Recordatorio a colaborador',
  contribution_received: 'Aportacion recibida',
  book_ready_review: 'Libro listo para revision',
  book_sent_print: 'Enviado a imprenta',
  order_confirmed: 'Pedido confirmado',
  book_shipped: 'Libro enviado',
  custom: 'Personalizado',
};

const RECIPIENT_LABELS = {
  client: 'Cliente',
  collaborator: 'Colaborador',
};

const VARIABLE_DESCRIPTIONS = {
  nombre_protagonista: 'Nombre del protagonista del libro',
  nombre_colaborador: 'Nombre del colaborador invitado',
  nombre_cliente: 'Nombre del cliente que encarga el libro',
  titulo_libro: 'Titulo del libro',
  enlace_formulario: 'URL del formulario de aportacion',
  enlace_revision: 'URL para revisar la prueba del libro',
  fecha_limite: 'Fecha limite para aportar',
  numero_pedido: 'Numero del pedido',
  importe: 'Importe total del pedido',
  numero_seguimiento: 'Numero de seguimiento del envio',
  transportista: 'Nombre del transportista',
  fecha_entrega: 'Fecha estimada de entrega',
  enlace_seguimiento: 'URL de seguimiento del envio',
};

export const NotificationTemplatesPanel = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [testEmailDialog, setTestEmailDialog] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [activeTab, setActiveTab] = useState('templates');
  const [emailLogs, setEmailLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/notification-templates`, { withCredentials: true });
      setTemplates(res.data);
    } catch {
      toast.error(t('notifications.errorLoadingTemplates'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const fetchEmailLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-logs?limit=50`, { withCredentials: true });
      setEmailLogs(res.data);
    } catch {
      toast.error('Error al cargar registros de email');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailDialog || !testEmail) return;
    setSendingTest(true);
    try {
      await axios.post(`${API_URL}/api/admin/send-test-email`, {
        template_id: testEmailDialog.template_id,
        to_email: testEmail,
      }, { withCredentials: true });
      toast.success(`${t('notifications.testEmailSent')} ${testEmail}`);
      setTestEmailDialog(null);
      setTestEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('notifications.testEmailError'));
    } finally {
      setSendingTest(false);
    }
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      if (editingTemplate.template_id) {
        await axios.put(
          `${API_URL}/api/admin/notification-templates/${editingTemplate.template_id}`,
          editingTemplate,
          { withCredentials: true }
        );
        toast.success(t('notifications.templateUpdated'));
      } else {
        await axios.post(
          `${API_URL}/api/admin/notification-templates`,
          editingTemplate,
          { withCredentials: true }
        );
        toast.success(t('notifications.templateCreated'));
      }
      setEditingTemplate(null);
      fetchTemplates();
    } catch {
      toast.error(t('notifications.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/api/admin/notification-templates/${deleteId}`, { withCredentials: true });
      toast.success(t('notifications.templateDeleted'));
      setDeleteId(null);
      fetchTemplates();
    } catch {
      toast.error(t('notifications.deleteError'));
    }
  };

  const insertVariable = (field, variable) => {
    const tag = `{{${variable}}}`;
    navigator.clipboard.writeText(tag);
    toast.success(`{{${variable}}} copiado al portapapeles`);
  };

  const newTemplate = () => {
    setEditingTemplate({
      name: '',
      event_key: 'custom',
      recipient_type: 'client',
      channels: {
        email: { subject: '', body: '' },
        whatsapp: { body: '' },
      },
      variables: [],
      is_active: true,
    });
  };

  const updateChannel = (channel, field, value) => {
    setEditingTemplate(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: { ...prev.channels[channel], [field]: value },
      },
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('notifications.loadingTemplates')}</div>;
  }

  const clientTemplates = templates.filter(t => t.recipient_type === 'client').sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));
  const collabTemplates = templates.filter(t => t.recipient_type === 'collaborator').sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));

  const renderTemplateCard = (tmpl) => {
    const isExpanded = expandedId === tmpl.template_id;
    return (
      <Card key={tmpl.template_id} className={`transition-all ${!tmpl.is_active ? 'opacity-60' : ''}`} data-testid={`notif-template-${tmpl.template_id}`}>
        <CardContent className="p-0">
          <div
            className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => setExpandedId(isExpanded ? null : tmpl.template_id)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {tmpl.sort_order && (
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center">{tmpl.sort_order}</span>
                )}
                <span className="font-medium text-sm truncate">{tmpl.name}</span>
                {!tmpl.is_active && <Badge variant="secondary" className="text-[10px]">{t('notifications.inactive')}</Badge>}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="outline" className="text-[10px]">{EVENT_LABELS[tmpl.event_key] || tmpl.event_key}</Badge>
                <span className="flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  <MessageCircle className="w-3 h-3" />
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                size="icon" variant="ghost" className="h-7 w-7 text-blue-500"
                onClick={(e) => { e.stopPropagation(); setTestEmailDialog(tmpl); setTestEmail(''); }}
                title="Enviar email de prueba"
                data-testid={`test-email-${tmpl.template_id}`}
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="icon" variant="ghost" className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); setEditingTemplate({ ...tmpl }); }}
                data-testid={`edit-notif-${tmpl.template_id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="icon" variant="ghost" className="h-7 w-7 text-destructive"
                onClick={(e) => { e.stopPropagation(); setDeleteId(tmpl.template_id); }}
                data-testid={`delete-notif-${tmpl.template_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </div>
          </div>
          {isExpanded && (
            <div className="px-4 pb-4 border-t space-y-3 pt-3">
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Mail className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs font-medium">{t('notifications.emailChannel')}</span>
                </div>
                <div className="bg-muted/30 rounded-md p-3 space-y-1">
                  <p className="text-xs"><strong>{t('notifications.subject')}:</strong> {tmpl.channels?.email?.subject || '-'}</p>
                  <p className="text-xs whitespace-pre-wrap text-muted-foreground">{tmpl.channels?.email?.body || '-'}</p>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <MessageCircle className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-xs font-medium">{t('notifications.whatsappChannel')}</span>
                </div>
                <div className="bg-green-50 rounded-md p-3">
                  <p className="text-xs whitespace-pre-wrap">{tmpl.channels?.whatsapp?.body || '-'}</p>
                </div>
              </div>
              {tmpl.variables?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {tmpl.variables.map(v => (
                    <Badge key={v} variant="secondary" className="text-[10px] font-mono">{`{{${v}}}`}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t('notifications.templatesTitle')}</h3>
          <p className="text-sm text-muted-foreground">{t('notifications.templatesDesc')}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-0.5">
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'templates' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setActiveTab('templates')}
              data-testid="notif-tab-templates"
            >
              {t('notifications.templates')}
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'logs' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => { setActiveTab('logs'); fetchEmailLogs(); }}
              data-testid="notif-tab-logs"
            >
              {t('notifications.sendLogs')}
            </button>
          </div>
          {activeTab === 'templates' && (
            <Button size="sm" onClick={newTemplate} className="gap-1.5" data-testid="new-notification-btn">
              <Plus className="w-3.5 h-3.5" /> {t('notifications.newTemplate')}
            </Button>
          )}
        </div>
      </div>

      {activeTab === 'templates' && (
        <>
          {/* Client notifications */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-blue-500" />
              <h4 className="font-medium text-sm">{t('notifications.clientNotifications')}</h4>
              <Badge variant="outline" className="text-[10px]">{clientTemplates.length}</Badge>
            </div>
            <div className="space-y-2">
              {clientTemplates.map(renderTemplateCard)}
              {clientTemplates.length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">{t('notifications.noClientTemplates')}</p>
              )}
            </div>
          </div>

          {/* Collaborator notifications */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-orange-500" />
              <h4 className="font-medium text-sm">{t('notifications.collaboratorNotifications')}</h4>
              <Badge variant="outline" className="text-[10px]">{collabTemplates.length}</Badge>
            </div>
            <div className="space-y-2">
              {collabTemplates.map(renderTemplateCard)}
              {collabTemplates.length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">{t('notifications.noCollabTemplates')}</p>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'logs' && (
        <div className="space-y-3">
          {logsLoading ? (
            <div className="text-center py-8 text-muted-foreground text-sm">{t('notifications.loadingLogs')}</div>
          ) : emailLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">{t('notifications.noLogs')}</div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="email-logs-table">
                <thead className="bg-muted/50 text-xs">
                  <tr>
                    <th className="text-left p-3 font-medium">{t('notifications.colStatus')}</th>
                    <th className="text-left p-3 font-medium">{t('notifications.colRecipient')}</th>
                    <th className="text-left p-3 font-medium">{t('notifications.colSubjectEvent')}</th>
                    <th className="text-left p-3 font-medium">{t('notifications.colType')}</th>
                    <th className="text-left p-3 font-medium">{t('notifications.colDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLogs.map((log, i) => (
                    <tr key={log.log_id || i} className="border-t hover:bg-muted/20 transition-colors">
                      <td className="p-3">
                        {log.status === 'sent' ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </td>
                      <td className="p-3 text-xs font-mono">{log.to_email}</td>
                      <td className="p-3 text-xs">{log.subject || log.event_key || '-'}</td>
                      <td className="p-3">
                        <Badge variant={log.is_test ? 'secondary' : 'default'} className="text-[10px]">
                          {log.is_test ? t('notifications.test') : t('notifications.real')}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {log.sent_at ? new Date(log.sent_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && setEditingTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate?.template_id ? t('notifications.editTemplate') : t('notifications.newTemplate')}</DialogTitle>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-5">
              {/* Basic info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs mb-1.5 block">Nombre</Label>
                  <Input
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Nombre de la plantilla"
                    data-testid="notif-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs mb-1.5 block">Destinatario</Label>
                    <Select
                      value={editingTemplate.recipient_type}
                      onValueChange={(v) => setEditingTemplate(prev => ({ ...prev, recipient_type: v }))}
                    >
                      <SelectTrigger data-testid="notif-recipient-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client">{t('notifications.client')}</SelectItem>
                        <SelectItem value="collaborator">{t('notifications.collaborator')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs mb-1.5 block">{t('notifications.order')}</Label>
                    <Input
                      type="number"
                      min={1}
                      value={editingTemplate.sort_order || ''}
                      onChange={(e) => setEditingTemplate(prev => ({ ...prev, sort_order: parseInt(e.target.value) || null }))}
                      placeholder="1, 2, 3..."
                      data-testid="notif-sort-order"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs mb-1.5 block">{t('notifications.event')}</Label>
                  <Select
                    value={editingTemplate.event_key}
                    onValueChange={(v) => setEditingTemplate(prev => ({ ...prev, event_key: v }))}
                  >
                    <SelectTrigger data-testid="notif-event-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(EVENT_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editingTemplate.is_active}
                      onCheckedChange={(v) => setEditingTemplate(prev => ({ ...prev, is_active: v }))}
                      data-testid="notif-active-switch"
                    />
                    <Label className="text-xs">{editingTemplate.is_active ? t('notifications.active') : t('notifications.inactive')}</Label>
                  </div>
                </div>
              </div>

              {/* Variables */}
              <div>
                <Label className="text-xs mb-1.5 block">{t('notifications.availableVariables')}</Label>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(VARIABLE_DESCRIPTIONS).map(([key, desc]) => (
                    <button
                      key={key}
                      type="button"
                      className="flex items-center gap-1 px-2 py-1 rounded-md bg-muted hover:bg-muted/80 transition-colors group"
                      onClick={() => insertVariable('any', key)}
                      title={desc}
                    >
                      <span className="text-[10px] font-mono text-primary">{`{{${key}}}`}</span>
                      <Copy className="w-2.5 h-2.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Email channel */}
              <div className="space-y-3 p-4 rounded-lg border bg-blue-50/30">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  <span className="font-medium text-sm">{t('notifications.emailChannel')}</span>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">{t('notifications.emailSubject')}</Label>
                  <Input
                    value={editingTemplate.channels?.email?.subject || ''}
                    onChange={(e) => updateChannel('email', 'subject', e.target.value)}
                    placeholder={t('notifications.emailSubjectPlaceholder')}
                    data-testid="notif-email-subject"
                  />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">{t('notifications.body')}</Label>
                  <Textarea
                    value={editingTemplate.channels?.email?.body || ''}
                    onChange={(e) => updateChannel('email', 'body', e.target.value)}
                    placeholder={t('notifications.emailBodyPlaceholder')}
                    rows={8}
                    className="font-mono text-xs"
                    data-testid="notif-email-body"
                  />
                </div>
              </div>

              {/* WhatsApp channel */}
              <div className="space-y-3 p-4 rounded-lg border bg-green-50/30">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-500" />
                  <span className="font-medium text-sm">{t('notifications.whatsappChannel')}</span>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">{t('notifications.message')}</Label>
                  <Textarea
                    value={editingTemplate.channels?.whatsapp?.body || ''}
                    onChange={(e) => updateChannel('whatsapp', 'body', e.target.value)}
                    placeholder={t('notifications.whatsappPlaceholder')}
                    rows={4}
                    className="font-mono text-xs"
                    data-testid="notif-whatsapp-body"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingTemplate(null)} className="gap-1.5">
                  <X className="w-3.5 h-3.5" /> {t('common.cancel')}
                </Button>
                <Button onClick={handleSave} disabled={saving || !editingTemplate.name} className="gap-1.5" data-testid="save-notification-btn">
                  <Save className="w-3.5 h-3.5" /> {saving ? t('notifications.saving') : t('common.save')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test email dialog */}
      <Dialog open={!!testEmailDialog} onOpenChange={(open) => !open && setTestEmailDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-4 h-4 text-blue-500" />
              {t('notifications.sendTestTitle')}
            </DialogTitle>
          </DialogHeader>
          {testEmailDialog && (
            <div className="space-y-4">
              <div className="bg-muted/30 rounded-md p-3">
                <p className="text-xs font-medium mb-1">{t('notifications.template')}: {testEmailDialog.name}</p>
                <p className="text-xs text-muted-foreground">{t('notifications.testEmailDesc')}</p>
              </div>
              <div>
                <Label className="text-xs mb-1.5 block">Email destinatario</Label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@ejemplo.com"
                  data-testid="test-email-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setTestEmailDialog(null)}>{t('common.cancel')}</Button>
                <Button
                  size="sm"
                  onClick={handleSendTestEmail}
                  disabled={sendingTest || !testEmail}
                  className="gap-1.5"
                  data-testid="confirm-send-test-btn"
                >
                  <Send className="w-3.5 h-3.5" />
                  {sendingTest ? t('notifications.sending') : t('notifications.sendTest')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('notifications.deleteTemplate')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('notifications.deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
